import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import { trackEvent } from '../analytics'
import { API_BASE } from '../config'
import { SafeImage } from '../components/ui/SafeImage'

interface InsightItem {
  id: number
  title: string
  slug: string
  summary?: string
  cover?: string
  publishedAt?: string
}

export default function InsightsPage() {
  const [insights, setInsights] = useState<InsightItem[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    fetch(`${API_BASE}/api/public/insights`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled || !data?.success || !Array.isArray(data.insights)) return
        setInsights(data.insights)
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [])

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.35 }}
      className="pt-28 sm:pt-32 pb-16 sm:pb-20 px-4 sm:px-6 lg:px-8"
    >
      <div className="container-wide">
        <motion.div
          className="text-center mb-10 sm:mb-14"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-primary dark:text-white mb-3 sm:mb-4">
            Knowledge & Insights
          </h1>
          <p className="section-sub max-w-2xl mx-auto">
            We regularly share insights on lead generation systems, Meta Ads strategies, sales conversion frameworks, and digital growth for high-value industries.
          </p>
        </motion.div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="card-glass p-5 sm:p-6 h-56 rounded-2xl animate-pulse bg-slate-200/30 dark:bg-slate-700/20" />
            ))}
          </div>
        ) : insights.length === 0 ? (
          <p className="text-center text-slate-500 dark:text-slate-400">No insights yet. Check back soon.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {insights.map((insight, i) => (
              <Link key={insight.id} to={`/insights/${insight.slug}`}>
                <motion.article
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 + i * 0.05 }}
                  className="card-glass p-0 overflow-hidden hover:shadow-card-hover dark:hover:shadow-card-hover transition-all duration-300 group h-full flex flex-col"
                >
                  <div className="aspect-video overflow-hidden bg-slate-100 dark:bg-slate-800">
                    <SafeImage
                      src={insight.cover}
                      alt=""
                      label={insight.title}
                      tone="document"
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-5 sm:p-6 flex flex-col flex-1">
                    <h2 className="text-lg sm:text-xl font-semibold text-slate-800 dark:text-slate-100 group-hover:text-primary dark:group-hover:text-accent transition-colors line-clamp-2">
                      {insight.title}
                    </h2>
                    {insight.summary && (
                      <p className="mt-2 text-slate-600 dark:text-slate-400 text-sm line-clamp-2 flex-1">{insight.summary}</p>
                    )}
                    <span className="mt-4 inline-flex items-center gap-2 text-sm text-primary dark:text-accent font-medium">
                      Read more <ArrowRight size={14} strokeWidth={2} className="group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </motion.article>
              </Link>
            ))}
          </div>
        )}

        <motion.div className="mt-12 text-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
          <Link
            to="/contact"
            className="btn-primary"
            onClick={() => trackEvent('cta_click', { cta_name: 'book_strategy_call', from: 'insights_page' })}
          >
            Book Strategy Call
          </Link>
        </motion.div>
      </div>
    </motion.div>
  )
}
